import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { GlobalConfig, ToastrService } from "ngx-toastr";
import { cloneDeep } from "lodash";

import { CustomToastrComponent } from "app/menu/custom-toastr/custom-toastr.component";
import { CurrentService } from "app/services/current.service";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private options: GlobalConfig;
  private isRedirecting = false; // ✅ Flag pour éviter les redirections multiples

  constructor(
    private router: Router,
    private currentService: CurrentService,
    private _toastr: ToastrService
  ) {
    this.options = this._toastr.toastrConfig;
  }

  toastrDisplay(type, message, title) {
    const customToastrRef = cloneDeep(this.options);
    customToastrRef.toastComponent = CustomToastrComponent;
    customToastrRef.closeButton = true;
    customToastrRef.tapToDismiss = true;
    customToastrRef.progressBar = true;
    customToastrRef.toastClass = "toast ngx-toastr";

    switch (type) {
      case "error":
        this._toastr.error(message, title, customToastrRef);
        break;
      case "info":
        this._toastr.info(message, title, customToastrRef);
        break;
      case "success":
        this._toastr.success(message, title, customToastrRef);
        break;
      case "warning":
        this._toastr.warning(message, title, customToastrRef);
        break;
      default:
        this._toastr.show(message, title, customToastrRef);
        break;
    }
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err) => {
        // ✅ Ignorer les erreurs pendant la déconnexion
        if (this.currentService.isLoggingOut) {
          return throwError(err);
        }

        if (err.status === 401) {
          // ✅ Éviter les redirections multiples
          if (this.isRedirecting) {
            return throwError(err);
          }

          this.isRedirecting = true;

          this.toastrDisplay(
            "warning",
            "Votre session a expiré. Veuillez vous reconnecter.",
            "Session expirée"
          );

          this.currentService.currentUser.next(null);
          this.currentService.currentTenant.next(null);

          // ✅ FIX : Nettoyer l'URL et éviter la boucle infinie
          let currentUrl = this.router.url;

          // ✅ Si l'URL contient déjà "/authentication/login", ne pas ajouter de returnUrl
          if (currentUrl.includes("/authentication/login")) {
            this.router.navigate(["/authentication/login"]);
            this.isRedirecting = false;
            return throwError(err);
          }

          // ✅ Extraire uniquement la route de base (sans query params)
          const urlWithoutParams = currentUrl.split("?")[0];

          // ✅ Ne pas rediriger vers les pages d'authentification
          const authRoutes = ["/authentication/", "/pages/"];
          if (authRoutes.some((route) => urlWithoutParams.includes(route))) {
            this.router.navigate(["/authentication/login"]);
            this.isRedirecting = false;
            return throwError(err);
          }

          // ✅ Redirection propre avec returnUrl simple
          this.router
            .navigate(["/authentication/login"], {
              queryParams: {
                returnUrl: urlWithoutParams, // ✅ URL propre sans params
                tenantId: this.currentService?.currentTenant?.getValue()?.id,
              },
            })
            .then(() => {
              this.isRedirecting = false;
            });
        } else if (err.status === 403) {
          this.toastrDisplay(
            "error",
            "Vous n'êtes pas autorisé à effectuer cette action.",
            "Erreur d'autorisation"
          );
        } else if (err.status === 404) {
          this.toastrDisplay(
            "info",
            "La ressource demandée est introuvable.",
            "Ressource introuvable"
          );
        } else if (err.status === 500) {
          this.toastrDisplay(
            "error",
            "Une erreur s'est produite. Veuillez réessayer plus tard.",
            "Erreur interne du serveur"
          );
        } else {
          this.toastrDisplay(
            err.error?.type || "error",
            err.error?.message || err.statusText || "Une erreur s'est produite.",
            err.error?.title || `Erreur ${err.status}`
          );
        }

        return throwError(err);
      })
    );
  }
}
